
<template>
  <div class="full-height-black-bg">
    <div ref="threeContainer" class="three-background">
      <div v-if="error" class="error-message">{{ error }}</div>
      <div v-if="loading" class="loading-message">Loading 3D model...</div>
    </div>
  </div>
</template>>

<script>
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';

import { markRaw } from 'vue';

export default {
  name: 'ThreeBackground',
  props: {
    modelPath: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      scene: null,
      camera: null,
      renderer: null,
      cube: null,
      models: [],
      loading: false,
      error: null,
      isReady: false,
      isDestroyed: false,
      containerResizeObserver: null,
      // Camera focus properties
      focusedCameraPosition: new THREE.Vector3(),
      focusedLookAt: new THREE.Vector3(),
      isFocused: false,
      // Add smooth camera transition properties
      cameraTransition: {
        isAnimating: false,
        animationId: null,
        startPosition: new THREE.Vector3(),
        startLookAt: new THREE.Vector3(),
        targetPosition: new THREE.Vector3(),
        targetLookAt: new THREE.Vector3(),
        duration: 800, // Shorter duration for smoother transitions
        startTime: 0
      },

      // Mouse interaction properties
      mouse: {
        x: 0,
        y: 0,
        normalizedX: 0,
        normalizedY: 0,
        targetX: 0,
        targetY: 0,
        smoothFactor: 0.05, // Increased for more noticeable effect
        intensity: 0.3,     // Increased for more noticeable effect
        cameraInfluence: 0.5, // Increased for more noticeable effect
        modelInfluence: 0.8,  // Increased for more noticeable effect
      },
      animationId: null,
      originalModelPositions: [],
      originalCameraPosition: new THREE.Vector3(),
      originalCameraLookAt: new THREE.Vector3(),

      orbit: {
        t: 0,
        speed: 0.05,
        radius: 0,
        height: 0,
        target: new THREE.Vector3(),
        damping: 0.08,
        currentPos: new THREE.Vector3(),
        initialPos: new THREE.Vector3(),
        startAngle: 0,
      },
      modelScaleMap: {
        '/models/sonata2.glb': 1.5,
        '/models/octave2.glb': 1.5,
        '/models/pegasus2.glb': 1.1,
        '/models/addon2.glb': 0.5,
      },
      useHardcodedScales: true,
    };
  },
  mounted() {
    console.log('ThreeBackground mounted');
    this.$nextTick(() => {
      this.initThree();
    });
  },
  beforeUnmount() {
    console.log('ThreeBackground beforeUnmount');
    this.cleanup();
  },
  methods: {
    initThree() {
      if (this.isDestroyed) return;

      const container = this.$refs.threeContainer;
      if (!container) {
        console.error('Container not found');
        this.error = 'Container not found';
        return;
      }

      try {
        // Scene
        this.scene = markRaw(new THREE.Scene());

        // Camera
        const containerRect = container.getBoundingClientRect();
        if (containerRect.width === 0 || containerRect.height === 0) {
          setTimeout(() => {
            if (!this.isDestroyed) this.initThree();
          }, 100);
          return;
        }

        this.camera = markRaw(new THREE.PerspectiveCamera(
            45,
            containerRect.width / containerRect.height,
            0.1,
            5000
        ));
        this.camera.updateProjectionMatrix();

        // Renderer
        this.renderer = markRaw(new THREE.WebGLRenderer({
          antialias: true,
          alpha: true,
          powerPreference: "high-performance"
        }));
        this.renderer.setSize(containerRect.width, containerRect.height);
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        this.renderer.setClearColor(0x000000, 0);
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        if ('outputColorSpace' in this.renderer) {
          this.renderer.outputColorSpace = THREE.SRGBColorSpace;
        } else {
          this.renderer.outputEncoding = THREE.sRGBEncoding;
        }
        this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
        this.renderer.toneMappingExposure = 1.5;
        this.renderer.gammaFactor = 5.2;

        container.appendChild(this.renderer.domElement);

        this.setupLights();
        // REMOVED: this.setupMouseListeners(); <- This was causing the error

        // Force load models even if no modelPath is provided
        this.loadMultipleGLBModels();

        this.setupResizeObserver();

        this.isReady = true;
      } catch (err) {
        console.error('Error initializing Three.js:', err);
        this.error = `Failed to initialize 3D scene: ${err.message}`;
      }
    },

    updateMousePosition(normalizedX, normalizedY) {
      if (this.isDestroyed) return;

      // Apply intensity and set targets
      this.mouse.targetX = normalizedX * this.mouse.intensity;
      this.mouse.targetY = normalizedY * this.mouse.intensity;

      // Store normalized values
      this.mouse.normalizedX = normalizedX;
      this.mouse.normalizedY = normalizedY;
    },

    resetMousePosition() {
      if (this.isDestroyed) return;

      this.mouse.targetX = 0;
      this.mouse.targetY = 0;
      this.mouse.normalizedX = 0;
      this.mouse.normalizedY = 0;
    },

    updateMouseEffects() {
      if (!this.isReady || this.isDestroyed) return;

      // Smooth interpolation for mouse effects
      const smoothFactor = this.mouse.smoothFactor;

      this.mouse.x += (this.mouse.targetX - this.mouse.x) * smoothFactor;
      this.mouse.y += (this.mouse.targetY - this.mouse.y) * smoothFactor;


      // Apply camera movement based on mouse position (only if not in focused mode)
      if (this.camera && this.originalCameraPosition.length() > 0 && !this.isFocused) {
        const cameraOffset = this.mouse.cameraInfluence;

        // Create smooth camera movement
        const newCameraPos = this.originalCameraPosition.clone();
        newCameraPos.x += this.mouse.x * cameraOffset;
        newCameraPos.y += this.mouse.y * cameraOffset * 0.5;

        this.camera.position.lerp(newCameraPos, 0.15);

        // Update camera look-at with subtle movement
        const newLookAt = this.originalCameraLookAt.clone();
        newLookAt.x += this.mouse.x * cameraOffset * 0.3;
        newLookAt.y += this.mouse.y * cameraOffset * 0.2;

        this.camera.lookAt(newLookAt);
      }

      // Apply model movements (only if not in focused mode)
      if (!this.isFocused) {
        this.models.forEach((model, index) => {
          if (this.originalModelPositions[index]) {
            const originalPos = this.originalModelPositions[index];
            const influence = this.mouse.modelInfluence;

            // Create layered movement - models at different "depths" move differently
            const depthFactor = 1 + (index * 0.2);
            const moveIntensity = influence * depthFactor;

            // Apply smooth movement to models
            const targetPos = new THREE.Vector3(
                originalPos.x + this.mouse.x * moveIntensity,
                originalPos.y + this.mouse.y * moveIntensity * 0.3,
                originalPos.z + this.mouse.x * moveIntensity * 0.15
            );

            model.position.lerp(targetPos, 0.1);

            // Add subtle rotation based on mouse movement
            const rotationIntensity = 0.08;
            model.rotation.y = this.mouse.x * rotationIntensity;
            model.rotation.x = this.mouse.y * rotationIntensity * 0.5;

          }
        });
      }
    },

    startRenderLoop() {
      if (this.isDestroyed) return;

      console.log('Starting render loop...');

      const animate = () => {
        if (this.isDestroyed) return;

        this.animationId = requestAnimationFrame(animate);

        this.updateMouseEffects();
        this.renderOnce();
      };

      animate();
    },

    storeOriginalPositions() {

      // Store original camera position and look-at
      if (this.camera) {
        this.originalCameraPosition.copy(this.camera.position);
        this.originalCameraLookAt.copy(this.orbit.target);
        console.log('Camera positions stored:', {
          camera: this.originalCameraPosition,
          lookAt: this.originalCameraLookAt
        });
      }

      // Store original model positions
      this.originalModelPositions = this.models.map((model, index) => {
        const pos = model.position.clone();
        console.log(`Model ${index} original position:`, pos);
        return pos;
      });

      console.log(`Stored ${this.originalModelPositions.length} model positions`);
    },

    loadMultipleGLBModels() {
      if (this.isDestroyed) return;

      this.loading = true;
      const modelPaths = [
        '/models/octave2.glb',
        '/models/sonata2.glb',
        '/models/pegasus2.glb',
        '/models/addon2.glb'
      ];

      // Set up GLTFLoader with LOCAL Draco decoders (avoids CSP issues)
      const loader = new GLTFLoader();
      const dracoLoader = new DRACOLoader();
      dracoLoader.setDecoderPath('/draco/'); // served from public/draco/*
      dracoLoader.preload(); // initialize decoder once
      loader.setDRACOLoader(dracoLoader);

      let loaded = 0;
      const loadedScenes = [];

      if (this.cube) {
        this.scene.remove(this.cube);
        this.cube = null;
      }

      modelPaths.forEach((modelPath, index) => {
        loader.load(
            modelPath,
            (gltf) => {
              const model = markRaw(gltf.scene);
              this.processMaterials(model);

              if (this.useHardcodedScales && this.modelScaleMap[modelPath] != null) {
                model.scale.setScalar(this.modelScaleMap[modelPath]);
              }

              loadedScenes[index] = model;
              loaded++;

              console.log(`Model loaded: ${modelPath} (${loaded}/${modelPaths.length})`);

              if (loaded === modelPaths.length) {
                this.models.forEach(m => this.scene.remove(m));
                this.models = [];

                this.layoutSideBySide(loadedScenes, 2.0);

                this.loading = false;
                this.isReady = true;

                // Store original positions after layout
                this.storeOriginalPositions();

                // Start render loop for mouse effects
                this.startRenderLoop();

                dracoLoader.dispose();
                console.log('All models loaded and render loop started');
              }
            },
            undefined,
            (error) => {
              console.error('Error loading GLB:', error);
              loaded++;
              if (loaded === modelPaths.length && !loadedScenes.some(Boolean)) {
                this.error = 'Failed to load 3D models';
                this.loading = false;
                console.log('Failed to load models, adding test cube instead');
                this.addTestCube();
                this.startRenderLoop();

                dracoLoader.dispose();
              }
            }
        );
      });
    },

    layoutSideBySide(models, gap = 1.5) {
      console.log('Laying out models in 2x2 grid...');

      // If using hardcoded scales, keep current scales and position only.
      if (this.useHardcodedScales) {
        const modelInfos = models.map((m) => {
          const box = new THREE.Box3().setFromObject(m);
          const size = box.getSize(new THREE.Vector3());
          const center = box.getCenter(new THREE.Vector3());

          // Move model so its center is at origin for easier layout
          m.position.sub(center);

          const scaledWidth = size.x;
          const scaledHeight = size.y;

          return { model: m, width: scaledWidth, height: scaledHeight };
        });

        // Arrange in 2x2 grid
        const rows = 2;
        const cols = 2;
        const rowGap = gap * 1.2; // Vertical gap between rows
        const colGap = gap; // Horizontal gap between columns

        // Calculate positions for 2x2 grid
        modelInfos.forEach((info, index) => {
          const row = Math.floor(index / cols);
          const col = index % cols;

          // Calculate x position (horizontal)
          const totalRowWidth = cols * info.width + (cols - 1) * colGap;
          const startX = -totalRowWidth / 2;
          const x = startX + col * (info.width + colGap) + info.width / 2;

          // Calculate z position (depth/vertical in the grid)
          const totalGridDepth = rows * info.width + (rows - 1) * rowGap;
          const startZ = -totalGridDepth / 2;
          const z = startZ + row * (info.width + rowGap) + info.width / 2;

          // Position the model
          info.model.position.x = x;
          info.model.position.z = z;
          // Position models on the floor (half their height above y=0)
          info.model.position.y = info.height / 2;

          this.scene.add(info.model);
          this.models.push(info.model);
        });

        this.frameCurrentLayout();
        console.log(`Models added to scene in 2x2 grid: ${this.models.length}`);
        return;
      }

      // Normalize each model to a common target height (fallback behavior)
      const targetHeight = 1.0;
      const modelInfos = models.map((m) => {
        const box = new THREE.Box3().setFromObject(m);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());

        // Move model so its center is at origin for easier layout
        m.position.sub(center);

        // Scale so height matches targetHeight
        const ref = size.y > 0 ? size.y : Math.max(size.x, size.z, 1e-6);
        const s = targetHeight / ref;
        m.scale.setScalar(s);

        // Recompute dimensions after scaling
        const scaledWidth = size.x * s;
        const scaledHeight = size.y * s;

        return { model: m, width: scaledWidth, height: scaledHeight };
      });

      // Arrange in 2x2 grid for fallback behavior
      const rows = 2;
      const cols = 2;
      const rowGap = gap * 1.5; // Vertical gap between rows
      const colGap = gap; // Horizontal gap between columns

      modelInfos.forEach((info, index) => {
        const row = Math.floor(index / cols);
        const col = index % cols;

        // Calculate x position (horizontal)
        const totalRowWidth = cols * info.width + (cols - 1) * colGap;
        const startX = -totalRowWidth / 2;
        const x = startX + col * (info.width + colGap) + info.width / 2;

        // Calculate z position (depth/vertical in the grid)
        const totalGridDepth = rows * info.width + (rows - 1) * rowGap;
        const startZ = -totalGridDepth / 2;
        const z = startZ + row * (info.width + rowGap) + info.width / 2;

        // Position the model
        info.model.position.x = x;
        info.model.position.z = z;
        // Position models on the floor (half their height above y=0)
        info.model.position.y = info.height / 2;

        this.scene.add(info.model);
        this.models.push(info.model);
      });

      this.frameCurrentLayout();
      console.log(`Models added to scene in 2x2 grid: ${this.models.length}`);
    },

    frameCurrentLayout() {
      if (!this.models.length || !this.camera) return;

      const container = this.$refs.threeContainer;
      const rect = container.getBoundingClientRect();
      // Remove this unused variable - it's calculated but never used
      // const aspect = rect.width / Math.max(rect.height, 1);

      const box = new THREE.Box3();
      this.models.forEach(m => box.union(new THREE.Box3().setFromObject(m)));
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());

      // Start from a more frontal position instead of side
      const dir = new THREE.Vector3(1.0, 2.0, 0.0).normalize();

      // Remove this unused variable - it's calculated but never used
      // const fov = THREE.MathUtils.degToRad(this.camera.fov);
      const fitHeightDistance = (size.y / 2) / Math.tan(THREE.MathUtils.degToRad(this.camera.fov) / 2);
      const fitWidthDistance = (size.x / 2) / Math.tan(THREE.MathUtils.degToRad(this.camera.fov) / 2) / (rect.width / Math.max(rect.height, 1));

      const padding = 1.80;
      const distance = Math.max(fitHeightDistance, fitWidthDistance) * padding;

      this.camera.position.copy(center.clone().add(dir.multiplyScalar(distance)));
      this.camera.near = Math.max(distance - size.length() * 2, 0.01);
      this.camera.far = distance + size.length() * 2;
      this.camera.updateProjectionMatrix();
      this.camera.lookAt(center);

      // Initialize orbit params from layout
      const radius = Math.max(size.x, size.z) * 1.2 + size.y * 0.4;
      this.orbit.radius = Math.max(radius, 0.5);
      this.orbit.height = Math.max(size.y * 0.6, 0.3);
      this.orbit.target.copy(center);
      this.orbit.currentPos.copy(this.camera.position);
      this.orbit.initialPos.copy(this.camera.position); // Store the initial position

      // Calculate the starting angle based on the initial camera position
      const relativePos = this.camera.position.clone().sub(center);
      this.orbit.startAngle = Math.atan2(relativePos.z, relativePos.x);
      this.orbit.t = 0; // Reset animation time

      console.log('Camera framed to layout');
    },

    processMaterials(object) {
      let processedMeshes = 0;
      let processedMaterials = 0;

      object.traverse((child) => {
        if (child.isMesh) {
          processedMeshes++;
          child.castShadow = true;
          child.receiveShadow = true;

          console.log(`Processing mesh ${processedMeshes}:`, child.name || 'unnamed');

          if (child.material) {
            // Handle both single material and material arrays
            const materials = Array.isArray(child.material) ? child.material : [child.material];

            materials.forEach((material) => {
              processedMaterials++;
              console.log(`Processing material ${processedMaterials} (${material.type}):`, material);

              if (material.isMeshStandardMaterial || material.isMeshPhysicalMaterial) {
                // Log original material properties
                console.log(`Material properties - metalness: ${material.metalness}, roughness: ${material.roughness}`);

                // Process different types of textures
                const textureTypes = ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap', 'emissiveMap'];
                let hasTextures = false;

                textureTypes.forEach(textureType => {
                  if (material[textureType]) {
                    hasTextures = true;
                    console.log(`Found ${textureType}:`, material[textureType]);

                    material[textureType].flipY = false;
                    if (textureType === 'map' || textureType === 'emissiveMap') {
                      material[textureType].encoding = THREE.sRGBEncoding;
                    }
                    material[textureType].needsUpdate = true;
                  }
                });

                if (!hasTextures) {
                  console.warn(`Material ${processedMaterials} has NO textures - this might be why it looks plain`);
                  // Add a basic color if no textures
                  if (!material.color || (material.color.r === 1 && material.color.g === 1 && material.color.b === 1)) {
                    material.color.setHex(0x888888); // Set a gray color for visibility
                    console.log('Applied default gray color to material without textures');
                  }
                }

                // Adjust material properties for better appearance
                material.metalness = material.metalness || 0.1;
                material.roughness = material.roughness || 0.8;
                material.needsUpdate = true;

              } else if (material.isMeshBasicMaterial) {
                console.log('Processing basic material');
                if (material.map) {
                  material.map.flipY = false;
                  material.map.encoding = THREE.sRGBEncoding;
                  material.map.needsUpdate = true;
                  console.log('Processed basic material texture');
                } else {
                  console.warn('Basic material has no texture map');
                }
                material.needsUpdate = true;
              } else {
                console.warn(`Unhandled material type: ${material.type}`);
              }
            });
          } else {
            console.warn(`Mesh ${processedMeshes} has no material!`);
          }
        }
      });

      console.log(`Finished processing: ${processedMeshes} meshes, ${processedMaterials} materials`);
    },

    addTestCube() {
      if (this.isDestroyed) return;

      console.log('Adding test cube...');

      const geometry = markRaw(new THREE.BoxGeometry(1, 1, 1));
      const material = markRaw(new THREE.MeshPhongMaterial({
        color: 0x4fc3f7,
        transparent: true,
        opacity: 0.7
      }));

      this.cube = markRaw(new THREE.Mesh(geometry, material));
      this.cube.position.set(0, 0.5, 0); // Position cube on the floor
      this.cube.castShadow = true;
      this.scene.add(this.cube);

      console.log('Test cube added to scene at:', this.cube.position);
    },

    setupLights() {
      if (this.isDestroyed) return;

      // Enhanced ambient light for better overall illumination
      const ambientLight = markRaw(new THREE.AmbientLight(0xffffff, 0.8));
      this.scene.add(ambientLight);

      // Main directional light
      const directionalLight = markRaw(new THREE.DirectionalLight(0xffffff, 0.6));
      directionalLight.position.set(10, 10, 5);
      directionalLight.castShadow = true;
      directionalLight.shadow.mapSize.width = 2048;
      directionalLight.shadow.mapSize.height = 2048;
      directionalLight.shadow.camera.near = 0.1;
      directionalLight.shadow.camera.far = 50;
      directionalLight.shadow.camera.left = -10;
      directionalLight.shadow.camera.right = 10;
      directionalLight.shadow.camera.top = 10;
      directionalLight.shadow.camera.bottom = -10;
      this.scene.add(directionalLight);

      // Fill light from opposite side
      const fillLight = markRaw(new THREE.DirectionalLight(0xffffff, 0.3));
      fillLight.position.set(-5, 3, -5);
      this.scene.add(fillLight);

      // Additional light for better texture visibility
      const topLight = markRaw(new THREE.DirectionalLight(0xffffff, 0.2));
      topLight.position.set(0, 10, 0);
      this.scene.add(topLight);
    },

    setupResizeObserver() {
      if (!window.ResizeObserver) {
        window.addEventListener('resize', this.onWindowResize);
        return;
      }

      this.containerResizeObserver = new ResizeObserver((entries) => {
        if (this.isDestroyed) return;

        const entry = entries[0];
        if (entry && this.camera && this.renderer) {
          const { width, height } = entry.contentRect;
          if (width > 0 && height > 0) {
            this.camera.aspect = width / height;
            this.camera.updateProjectionMatrix();
            this.renderer.setSize(width, height);
            this.frameCurrentLayout();
            this.renderOnce();
          }
        }
      });

      this.containerResizeObserver.observe(this.$refs.threeContainer);
    },

    renderOnce() {
      if (this.renderer && this.scene && this.camera) {
        try {
          this.renderer.render(this.scene, this.camera);
        } catch (err) {
          console.error('Render error:', err);
        }
      }
    },

    onWindowResize() {
      if (this.isDestroyed || !this.camera || !this.renderer) return;

      const container = this.$refs.threeContainer;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        this.camera.aspect = rect.width / rect.height;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(rect.width, rect.height);
        this.frameCurrentLayout();
        this.renderOnce();
      }
    },
    focusOnModel(modelIndex) {
      if (!this.models[modelIndex] || !this.camera || this.isDestroyed) {
        console.warn(`Cannot focus on model ${modelIndex} - not available`);
        return;
      }

      // Get the target model
      const targetModel = this.models[modelIndex];

      // Calculate bounding box of the target model
      const box = new THREE.Box3().setFromObject(targetModel);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());

      // Position camera closer and more focused on this specific model
      const direction = new THREE.Vector3(0.5, 0.8, 1.2).normalize();
      const distance = Math.max(size.y, size.x, size.z) * 2.2; // Closer than normal

      const newCameraPos = center.clone().add(direction.multiplyScalar(distance));

      // Store the focused position as new target
      this.focusedCameraPosition = newCameraPos.clone();
      this.focusedLookAt = center.clone();
      this.isFocused = true;

      // Use smooth transition instead of immediate animation
      this.smoothTransitionToPosition(newCameraPos, center, 600); // Shorter duration for responsiveness
    },

    resetCameraFocus() {
      if (!this.camera || this.isDestroyed) return;

      this.isFocused = false;

      // Animate back to original framed position with smooth transition
      if (this.originalCameraPosition.length() > 0) {
        this.smoothTransitionToPosition(this.originalCameraPosition, this.originalCameraLookAt, 500);
      }
    },
    smoothTransitionToPosition(targetPosition, targetLookAt, duration = 800) {
      if (!this.camera || this.isDestroyed) return;

      // Cancel any existing camera animation
      if (this.cameraTransition.animationId) {
        cancelAnimationFrame(this.cameraTransition.animationId);
        this.cameraTransition.animationId = null;
      }

      // Set up transition parameters
      this.cameraTransition.startPosition.copy(this.camera.position);
      this.cameraTransition.startLookAt.copy(this.getCurrentLookAt());
      this.cameraTransition.targetPosition.copy(targetPosition);
      this.cameraTransition.targetLookAt.copy(targetLookAt);
      this.cameraTransition.duration = duration;
      this.cameraTransition.startTime = Date.now();
      this.cameraTransition.isAnimating = true;

      // Start smooth animation loop
      const animateCamera = () => {
        if (this.isDestroyed || !this.cameraTransition.isAnimating) return;

        const elapsed = Date.now() - this.cameraTransition.startTime;
        const progress = Math.min(elapsed / this.cameraTransition.duration, 1);

        // Use smooth easing for more natural movement
        const eased = this.easeInOutQuart(progress);

        // Interpolate position smoothly
        const currentPos = this.cameraTransition.startPosition.clone().lerp(this.cameraTransition.targetPosition, eased);
        const currentLookAt = this.cameraTransition.startLookAt.clone().lerp(this.cameraTransition.targetLookAt, eased);

        this.camera.position.copy(currentPos);
        this.camera.lookAt(currentLookAt);

        if (progress < 1) {
          this.cameraTransition.animationId = requestAnimationFrame(animateCamera);
        } else {
          // Animation completed
          this.cameraTransition.isAnimating = false;
          this.cameraTransition.animationId = null;
        }
      };

      animateCamera();
    },
    getCurrentLookAt() {
      // Get current camera look direction
      const direction = new THREE.Vector3();
      this.camera.getWorldDirection(direction);
      return this.camera.position.clone().add(direction.multiplyScalar(10));
    },

    // Smoother easing function for more natural camera movement
    easeInOutQuart(t) {
      return t < 0.5 ? 8 * t * t * t * t : 1 - Math.pow(-2 * t + 2, 4) / 2;
    },


    cleanup() {
      console.log('Cleaning up Three.js resources...');
      this.isDestroyed = true;

      // Cancel animation frame
      if (this.animationId) {
        cancelAnimationFrame(this.animationId);
        this.animationId = null;
      }

      // Note: No mouse listeners to clean up here since they're handled by parent component

      if (this.containerResizeObserver) {
        this.containerResizeObserver.disconnect();
        this.containerResizeObserver = null;
      } else {
        window.removeEventListener('resize', this.onWindowResize);
      }

      if (this.renderer) {
        if (this.renderer.domElement && this.renderer.domElement.parentNode) {
          this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
        }
        this.renderer.dispose();
        this.renderer = null;
      }

      if (this.scene) {
        this.scene.traverse((child) => {
          if (child.geometry) {
            child.geometry.dispose();
          }
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach(material => material.dispose());
            } else {
              child.material.dispose();
            }
          }
        });

        this.scene.clear();
        this.scene = null;
      }

      this.camera = null;
      this.cube = null;
      this.models = [];
      this.originalModelPositions = [];

      console.log('Three.js cleanup completed');
    }
  }
};
</script>

<style scoped>
.full-height-black-bg {
  height: 100%;
  background: rgb(28 28 28 / 88%);
}
.three-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  overflow: hidden;

}

.three-background canvas {
  display: block;
  width: 100% !important;
  height: 100% !important;
}

.error-message, .loading-message {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 15px 20px;
  border-radius: 6px;
  text-align: center;
  z-index: 1;
  font-size: 14px;
}

.error-message {
  color: #ff6b6b;
  background: rgba(0, 0, 0, 0.8);
  border: 1px solid #ff6b6b;
}

.loading-message {
  color: #4fc3f7;
  background: rgba(0, 0, 0, 0.8);
  border: 1px solid #4fc3f7;
}

.debug-info {
  position: absolute;
  top: 10px;
  left: 10px;
  color: #4fc3f7;
  background: rgba(0, 0, 0, 0.7);
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 11px;
  z-index: 1;
  border: 1px solid rgba(79, 195, 247, 0.3);
}
</style>